/**
 * Generation Schemas - Zero-Overhead Content Generation
 *
 * This module automatically extracts generation requirements from component
 * field definitions. Developers only need to set `metadata.source: "ai"` on
 * fields that need AI generation - everything else is automatic.
 *
 * FLOW:
 * 1. Developer adds field with `metadata: { source: "ai", description: "..." }`
 * 2. This module auto-extracts all AI fields for the generator
 * 3. Generator uses these schemas to build prompts and validate output
 */

import { AllComponentFields } from "./components.registry";
import type { EditorFieldGuardrails } from "../lib/editor-field-guidance";

/**
 * Field source types
 */
type FieldSource = "ai" | "context" | "manual" | "derived";

type FieldHydrationTransform = "sanitizeTrustBadge";
type FieldHydrationFallback = "none" | "businessTrustBadge";
type FieldOverridePolicy = "userWins" | "systemWins";

interface FieldHydrationPolicy {
  transform?: FieldHydrationTransform;
  fallback?: FieldHydrationFallback;
  overridePolicy?: FieldOverridePolicy;
}

/**
 * Schema for array item fields
 */
interface ArrayItemFieldMeta {
  fieldName: string;
  description: string;
  type?: string;
}

/**
 * Field metadata extracted from component definitions
 */
interface FieldGenerationMeta {
  fieldName: string;
  source: FieldSource;
  description: string;
  seo: boolean;
  key?: string;
  contextKey?: string;
  fieldType?: string;
  /**
   * Legal values for a select/radio field, copied straight off the registry
   * field definition. Present ONLY when the values are a closed set, which is
   * what lets a generated schema constrain a model to real options instead of
   * trusting it to invent a valid one.
   */
  options?: { label: string; value: unknown }[];
  promptGuidance?: string[];
  hydration?: FieldHydrationPolicy;
  guardrails?: EditorFieldGuardrails;
  /** True if this field is an array container */
  isArray?: boolean;
  /** Schema for items within an array field */
  arrayItemFields?: ArrayItemFieldMeta[];
}

/**
 * Schema for a single component's generation requirements
 */
interface ComponentGenerationSchema {
  componentType: string;
  fields: FieldGenerationMeta[];
  aiFields: FieldGenerationMeta[];
  contextFields: FieldGenerationMeta[];
}

/**
 * Only select/radio declare a closed value set; every other field type returns
 * undefined so callers can treat "has options" as "is a choice field".
 */
const extractFieldOptions = (
    fieldDef: any,
  ): { label: string; value: unknown }[] | undefined => {
    if (fieldDef?.type !== "select" && fieldDef?.type !== "radio") {
      return undefined;
    }
    if (!Array.isArray(fieldDef.options) || fieldDef.options.length === 0) {
      return undefined;
    }
    const options = fieldDef.options
      .filter((option: any) => option && typeof option === "object" && "value" in option)
      .map((option: any) => ({
        label: String(option.label ?? option.value),
        value: option.value,
      }));
    if (options.length > 0) {
      return options;
    }
    return undefined;
  },

  /**
   * Extract all fields requiring generation from a component's field definitions
   */
  extractFieldsFromDefinition = (fields: Record<string, any>): {
    fields: FieldGenerationMeta[];
    aiFields: FieldGenerationMeta[];
    contextFields: FieldGenerationMeta[];
  } => {
    const aiFields: FieldGenerationMeta[] = [],
      allFields: FieldGenerationMeta[] = [],
      contextFields: FieldGenerationMeta[] = [],

      processField = (fieldName: string, fieldDef: any) => {
        const metadata = fieldDef?.metadata;
        if (!metadata?.source) {
          return;
        }

        const arrayItemFields: ArrayItemFieldMeta[] = [],
          isArray = fieldDef.type === "array";

        // Extract array item field schemas
        if (isArray && fieldDef.arrayFields) {
          for (const [nestedName, nestedDef] of Object.entries(fieldDef.arrayFields)) {
            const nestedMeta = (nestedDef as any)?.metadata;
            arrayItemFields.push({
              description: nestedMeta?.description || `${nestedName} in ${fieldName}`,
              fieldName: nestedName,
              type: (nestedDef as any)?.type,
            });
          }
        }

        const meta: FieldGenerationMeta = {
          fieldName,
          source: metadata.source,
          description: metadata.description || `${fieldName} content`,
          seo: metadata.seo || false,
          key: metadata.key,
          contextKey: metadata.contextKey,
          fieldType: fieldDef.type,
          options: extractFieldOptions(fieldDef),
          promptGuidance: Array.isArray(metadata.promptGuidance)
            ? metadata.promptGuidance.filter(
                (line: unknown): line is string => typeof line === "string" && line.trim().length > 0,
              )
            : undefined,
          hydration: metadata.hydration,
          guardrails: metadata.guardrails,
          isArray,
          arrayItemFields: isArray ? arrayItemFields : undefined,
        };

        allFields.push(meta);

        if (metadata.source === "ai") {
          aiFields.push(meta);
        } else if (metadata.source === "context") {
          contextFields.push(meta);
        }

        // Also add nested array fields as separate entries (for backward compatibility)
        if (fieldDef.arrayFields) {
          for (const [nestedName, nestedDef] of Object.entries(fieldDef.arrayFields)) {
            const nestedMeta = (nestedDef as any)?.metadata;
            if (nestedMeta?.source === "ai") {
              aiFields.push({
                description: nestedMeta.description || `${nestedName} in ${fieldName}`,
                fieldName: `${fieldName}[].${nestedName}`,
                key: nestedMeta.key,
                seo: nestedMeta.seo || false,
                source: "ai",
              });
            }
          }
        }
      };

    for (const [fieldName, fieldDef] of Object.entries(fields)) {
      processField(fieldName, fieldDef);
    }

    return { aiFields, contextFields, fields: allFields };
  },

  /**
   * Auto-generated component schemas from AllComponentFields
   * This is rebuilt automatically when component definitions change.
   */
  buildAllSchemas = (): Record<string, ComponentGenerationSchema> => {
    const schemas: Record<string, ComponentGenerationSchema> = {};

    for (const [componentType, fields] of Object.entries(AllComponentFields)) {
      const {
        fields: allFields,
        aiFields,
        contextFields,
      } = extractFieldsFromDefinition(fields as Record<string, any>);
      schemas[componentType] = {
        aiFields,
        componentType,
        contextFields,
        fields: allFields,
      };
    }

    return schemas;
  },

  /**
   * All component generation schemas - auto-generated from field definitions
   */
  COMPONENT_SCHEMAS = buildAllSchemas(),

  /**
   * Get AI-required field names for a component
   */
  getAIFields = (componentType: string): string[] => COMPONENT_SCHEMAS[componentType]?.aiFields.map((f) => f.fieldName) || [],

  /**
   * Get detailed AI field metadata for a component
   */
  getAIFieldsMeta = (componentType: string): FieldGenerationMeta[] => COMPONENT_SCHEMAS[componentType]?.aiFields || [],

  /**
   * Fields whose legal values are a closed set — layout, variant, alignment. These
   * are the knobs a model can be handed safely: the option list becomes an enum in
   * the generated schema, so an invalid value is unrepresentable rather than merely
   * rejected after the fact.
   */
  getChoiceFieldsMeta = (componentType: string): FieldGenerationMeta[] => (COMPONENT_SCHEMAS[componentType]?.fields ?? []).filter(
    (field) => field.options !== undefined && field.options.length > 0,
  ),

  /**
   * Get all field metadata for a component, not just AI fields.
   */
  getComponentFieldsMeta = (componentType: string): FieldGenerationMeta[] => COMPONENT_SCHEMAS[componentType]?.fields || [],

  /**
   * Build AI prompt section for a component's fields
   * Returns field descriptions that can be added to an AI prompt
   */
  buildFieldPromptSection = (componentType: string): string => {
    const fields = getAIFieldsMeta(componentType);
    if (fields.length === 0) {
      return "";
    }

    return fields.map((f) => `- ${f.fieldName}: ${f.description}`).join("\n");
  },

  /**
   * Validate that props include all AI-required fields
   */
  validateProps = (
    componentType: string,
    props: Record<string, any>,
  ): { valid: boolean; missing: string[] } => {
    const required = getAIFields(componentType),
      missing = required.filter((field) => {
        // Handle nested fields like "services[].title"
        if (field.includes("[].")) {
          const [arrayField] = field.split("[]."),
            arr = props[arrayField];
          // Array fields are valid if array exists (items validated separately)
          return !Array.isArray(arr);
        }
        const value = props[field];
        return value === undefined || value === null || value === "";
      });

    return { missing, valid: missing.length === 0 };
  },

  /**
   * Get all components that have AI-generated fields
   */
  getComponentsWithAIFields = (): string[] => Object.entries(COMPONENT_SCHEMAS)
    .filter(([, schema]) => schema.aiFields.length > 0)
    .map(([type]) => type),

  /**
   * Debug: Print all AI fields across all components
   */
  debugPrintAllAIFields = (): void => {
    console.log("=== AI-Generated Fields by Component ===\n");
    for (const [type, schema] of Object.entries(COMPONENT_SCHEMAS)) {
      if (schema.aiFields.length === 0) {
        continue;
      }
      console.log(`${type}:`);
      for (const field of schema.aiFields) {
        console.log(`  - ${field.fieldName}: ${field.description}`);
      }
      console.log();
    }
  };

type ComponentType = keyof typeof COMPONENT_SCHEMAS;

export type {
  FieldSource,
  FieldHydrationTransform,
  FieldHydrationFallback,
  FieldOverridePolicy,
  FieldHydrationPolicy,
  ArrayItemFieldMeta,
  FieldGenerationMeta,
  ComponentGenerationSchema,
  ComponentType,
};
export {
  COMPONENT_SCHEMAS,
  getAIFields,
  getAIFieldsMeta,
  getChoiceFieldsMeta,
  getComponentFieldsMeta,
  buildFieldPromptSection,
  validateProps,
  getComponentsWithAIFields,
  debugPrintAllAIFields,
};
